import React from "react";
import LegalLayout from "@/components/LegalLayout";
import ContactFormLink from "@/components/ContactFormLink";

export default function ComplianceDisclosures() {
  return (
    <LegalLayout
      slug="compliance"
      eyebrow="Compliance"
      title="Compliance Disclosures"
      lastUpdated="February 3, 2026"
    >
      <p>
        Armstrong K9 Registry is a private digital pedigree
        service. This page sets out our posture toward various legal and
        regulatory frameworks that may apply to you as a user, and how we
        interact with them.
      </p>

      <h2>Not a government registry</h2>
      <p>
        We are a <strong>private</strong> registry. Our records do not
        replace, and are not endorsed by, any government animal-control
        agency, municipal pet-licensing authority, or state veterinary board.
        Check your local jurisdiction's licensing and vaccination
        requirements separately.
      </p>

      <h2>US state privacy laws</h2>
      <p>
        We provide the access, correction, deletion, and opt-out rights
        described in our <a href="/privacy">Privacy Policy</a> to all users,
        consistent with the following where applicable:
      </p>
      <ul>
        <li>
          California Consumer Privacy Act (CCPA) and California Privacy
          Rights Act (CPRA)
        </li>
      </ul>
      <p>
        We <strong>do not sell</strong> personal information and do not
        engage in "sharing" for cross-context behavioral advertising.
      </p>

      <h2>Children's privacy (COPPA)</h2>
      <p>
        The Registry is not directed at children under 13, and we do not
        knowingly collect their personal information. Users must be at
        least 18 to create an account.
      </p>

      <h2>PCI DSS</h2>
      <p>
        Card payments are processed exclusively by Stripe, a PCI-DSS Level 1
        service provider. Our systems operate outside PCI scope — we never
        store, process, or transmit full cardholder data. Stripe provides
        the payment form; we receive only tokens and last-four metadata.
      </p>

      <h2>Anti-money-laundering &amp; sanctions</h2>
      <p>
        Stripe performs know-your-customer (KYC) and sanctions screening on
        payouts. Transactions that Stripe flags as violating OFAC or similar
        regimes will be declined.
      </p>

      <h2>Tax</h2>
      <p>
        We charge the quoted USD price for digital registrations and
        subscriptions. Where sales tax is applicable based on your
        jurisdiction, it is collected through Stripe at checkout. You are
        responsible for reporting your own income from breeding or kennel
        operations under your local tax law — the Registry does not issue
        tax advice.
      </p>

      <h2>Export controls</h2>
      <p>
        Our software is of standard commercial grade and not subject to U.S.
        export licensing restrictions beyond those that apply generally to
        online services. You agree not to access the Registry from any
        jurisdiction currently under comprehensive U.S. sanctions.
      </p>

      <h2>Animal-welfare law</h2>
      <p>
        Nothing on the Registry exempts members from compliance with the
        U.S. Animal Welfare Act, state and municipal humane codes, breeder
        licensing statutes, vaccination mandates, or zoning rules governing
        breeding operations. See our{" "}
        <a href="/ethics">Ethical Considerations</a> page for the expectations
        we place on members above any legal minimum.
      </p>

      <h2>Law-enforcement requests</h2>
      <p>
        We respond to valid legal process (subpoena, court order, warrant)
        served by a U.S. authority of competent jurisdiction. Where
        permitted, we notify the affected user before disclosure. Emergency
        requests involving imminent harm are reviewed urgently.
      </p>

      <h2>Service availability</h2>
      <p>
        The Registry is offered on a commercially-reasonable-efforts basis.
        We target high uptime but do not provide a contractual SLA on the
        free tier. Enterprise kennels with availability requirements should
        contact us directly.
      </p>

      <h2>Contact a compliance officer</h2>
      <p>
        For regulated-entity requests (law enforcement, regulators,
        auditors): <a href="mailto:compliance@armstrongk9registry.com">compliance@armstrongk9registry.com</a>
        <ContactFormLink topic="compliance" subject="Compliance / regulatory inquiry" />.
      </p>
    </LegalLayout>
  );
}
